import { useRef } from 'react';
import { notification } from 'antd';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  OAuth2ThirdPartyEnum,
  useCacheOAuthAuthorizationCodeMutation,
  useExchangeOAuthAuthorizationCodeForTokensMutation,
  useExchangeOAuthAuthorizationCodeForTokensViaTransientTokenMutation,
  useLogOAuthAuthorizationErrorMutation,
} from '@/types/generated/graphql';
import { TRANSIENT_TOKEN } from '../constants/integration.constants';
import { cacheAddIntegration, cacheIncrementIntegrationCount } from '../integration-handler';
import { ExecuteMutationOptions } from '../integrations.type';
import Cookies from 'js-cookie';
import { cookieOptions } from '@shared/utils';
import { useTranslation } from 'react-i18next';

// Maps the lowercase `thirdPartyName` URL slug (as built by each provider's own OAuth2Client
// redirect URI, e.g. HubspotOAuth2Client's `?thirdPartyName=hubspot`) to the exact
// OAuth2ThirdPartyEnum value the GraphQL schema expects. Deliberately explicit rather than derived
// via a word-boundary `capitalize()` call: that happens to reproduce `Pipedrive` (a single word, so
// capitalizing its first letter is the whole job) but can never reproduce `HubSpot`'s internal
// capital S from the single lowercase word `hubspot` — confirmed live: the backend rejected
// `capitalize('hubspot')` === `"Hubspot"` with `VALIDATION_INVALID_TYPE_VARIABLE` since it isn't a
// member of `OAuth2ThirdPartyEnum`. `HubSpot` isn't in this file's generated `OAuth2ThirdPartyEnum`
// yet (codegen hasn't been run against a schema snapshot that has it) — cast as the raw literal
// until `pnpm generate` picks it up.
const THIRD_PARTY_NAME_BY_SLUG: Record<string, OAuth2ThirdPartyEnum> = {
  pipedrive: OAuth2ThirdPartyEnum.Pipedrive,
  pabblyconnect: OAuth2ThirdPartyEnum.PabblyConnect,
  hubspot: 'HubSpot' as OAuth2ThirdPartyEnum,
};

const resolveThirdPartyName = (slug: string | null): OAuth2ThirdPartyEnum =>
  THIRD_PARTY_NAME_BY_SLUG[(slug || 'pipedrive').toLowerCase()] ?? OAuth2ThirdPartyEnum.Pipedrive;

const executeMutation = async <TInput>({ canRun, input, mutation, isRunningRef }: ExecuteMutationOptions<TInput>) => {
  if (!canRun || isRunningRef.current) return;
  isRunningRef.current = true;
  await mutation({ variables: { input } });
};

export const useThirdPartyOAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRunningRef = useRef(false);
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const thirdPartyName = resolveThirdPartyName(searchParams.get('thirdPartyName'));
  const transientToken = Cookies.get(TRANSIENT_TOKEN);
  const { t } = useTranslation();

  const navigateToIntegrations = () => {
    navigate('/settings/integrations?tab=all');
    isRunningRef.current = false;
  };

  const [logOAuthAuthorizationError] = useLogOAuthAuthorizationErrorMutation({
    onCompleted: () => {
      navigateToIntegrations();
    },
    onError: () => {
      navigateToIntegrations();
    },
  });

  const [cacheOAuthAuthorizationCode] = useCacheOAuthAuthorizationCodeMutation({
    onCompleted: (data) => {
      Cookies.set(
        TRANSIENT_TOKEN,
        data?.cacheOAuthAuthorizationCode?.data?.transientToken || '',
        { ...cookieOptions, expires: 5 / (60 * 24) } //transient Token expires for 5 min
      );
      notification.info({
        message: t('please_login_to_complete_pipedrive_integration'),
      });
    },
    onError: (err) => {
      notification.error({ message: err?.message });
    },
  });

  const [exchangeOauthForToken] = useExchangeOAuthAuthorizationCodeForTokensMutation({
    onCompleted: (data) => {
      notification.success({ message: data?.exchangeOAuthAuthorizationCodeForTokens?.message });
      navigateToIntegrations();
    },
    onError: (err) => {
      notification.error({ message: err?.message });
      navigateToIntegrations();
    },
    update: (_, { data }) => {
      const mutationData = data?.exchangeOAuthAuthorizationCodeForTokens?.data;
      const integartionId = data?.exchangeOAuthAuthorizationCodeForTokens?.data?.id;
      cacheIncrementIntegrationCount(mutationData);
      cacheAddIntegration(integartionId);
    },
  });

  const [exchangeViaTransientToken] = useExchangeOAuthAuthorizationCodeForTokensViaTransientTokenMutation({
    onCompleted: (data) => {
      navigateToIntegrations();
      Cookies.remove(TRANSIENT_TOKEN, cookieOptions);
      notification.success({
        message: data?.exchangeOAuthAuthorizationCodeForTokensViaTransientToken?.message,
      });
    },
    onError: (err) => {
      Cookies.remove(TRANSIENT_TOKEN, cookieOptions);
      notification.error({ message: err?.message });
      navigateToIntegrations();
    },
  });

  const handleExchangeOauthForToken = () =>
    executeMutation({
      canRun: !!code,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        code: code || '',
        ...(state && { state }),
      },
      mutation: exchangeOauthForToken,
      isRunningRef,
    });

  const handleLogOAuthAuthorizationError = () =>
    executeMutation({
      canRun: !!error,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        state: state || '',
        error,
      },
      mutation: logOAuthAuthorizationError,
      isRunningRef,
    });

  const handleCacheOAuth = () =>
    executeMutation({
      canRun: !!code,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        code: code || '',
      },
      mutation: cacheOAuthAuthorizationCode,
      isRunningRef,
    });

  const handleExchangeViaTransientToken = () =>
    executeMutation({
      canRun: !!transientToken,
      input: {
        thirdPartyName: thirdPartyName as OAuth2ThirdPartyEnum,
        transientToken,
      },
      mutation: exchangeViaTransientToken,
      isRunningRef,
    });

  return {
    thirdPartyOAuthCode: code,
    handleExchangeOauthForToken,
    handleLogOAuthAuthorizationError,
    handleCacheOAuth,
    handleExchangeViaTransientToken,
  };
};
